/**
 * Landing Page Features Section
 * 
 * Displays hotel features/amenities with icons
 */
import { useEffect, useRef, useState } from 'react';

export default function LandingFeaturesSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update visibility based on whether element is in viewport
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    { icon: '/wifi.svg', label: 'WIFI' },
    { icon: '/parking.svg', label: 'PARKING' },
    { icon: '/room dinning.svg', label: 'IN ROOM DINING' },
    { icon: '/guest room.svg', label: 'GUEST ROOMS' },
    { icon: '/wifi.svg', label: 'MASSAGE' },
  ];

  return (
    <div 
      ref={sectionRef}
      className="w-full min-h-[40vh] md:min-h-[30vh] py-8 md:py-12 flex items-center justify-center" 
      style={{ backgroundColor: '#000000' }}
    >
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Title */}
        <div className="mb-6 md:mb-8 text-center md:text-left">
          <h2 
            className={`text-xl sm:text-2xl md:text-3xl font-bold text-white transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'
            }`}
          >
            The essentials
          </h2>
        </div>
        
        {/* Icons - Grid layout for mobile, flex for desktop */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:items-center md:justify-between gap-6 sm:gap-8 md:gap-12 lg:gap-16">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className={`flex flex-col items-center space-y-2 sm:space-y-3 transition-all duration-700 ease-out ${
                isVisible 
                  ? 'opacity-100 scale-100 translate-z-0' 
                  : 'opacity-0 scale-50'
              }`}
              style={{ 
                transitionDelay: `${index * 100}ms`,
                transform: isVisible ? 'perspective(1000px) translateZ(0) scale(1)' : 'perspective(1000px) translateZ(-200px) scale(0.5)'
              }}
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 flex items-center justify-center">
                <img
                  src={feature.icon}
                  alt={feature.label}
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="font-semibold text-xs sm:text-sm md:text-base uppercase tracking-wide text-center text-white">
                {feature.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

